import React from 'react';

import { useActionCard } from '~/components/ClaimPage/CliveApp/CliveInsights/hooks/useActionCard';

import ActionConfidence from '../../ActionConfidence';
import ActionGestures from '../../ActionGestures';
import ActionHistory from '../../ActionHistory';
import { hasRevisions } from '../../ActionHistory/utils';
import ActionStateChip from '../../ActionStateChip';
import { ACTION_NEEDS_ATTENTION, ACTION_STATE_APPEARANCE, ACTIVITY_VALUE_VISIBLE_LINES } from '../../constants';
import PayloadList from '../../PayloadList';
import RejectionReason from '../../RejectionReason';
import SettledActionPreview, { isPreviewableOnceSettled } from '../../SettledActionPreview';
import { TEXTS } from '../../texts';
import {
  actionReasonLabel,
  actionRejectionReason,
  actionTitle,
  actionWaitingReason,
  activityCardElementId,
  logEntryElementId,
} from '../../utils';
import ActivityCard from '../ActivityCard';
import type { RecommendationRow } from '../types';

interface ActionCardProps {
  row: RecommendationRow;
  /** Refetches the claim's actions, which both panes resolve from. */
  onActionUpdate: () => Promise<void>;
  /** Brings the action's log entry into view and marks it selected. */
  onSelectLogEntry: (row: RecommendationRow) => void;
  /** Rings the card: the log last took the reader here. */
  isSelected: boolean;
  onDeselect: () => void;
}

/**
 * A recommendation Clive raised, as a pointer into the log — and, while it is waiting, the place
 * to act on it.
 *
 * **A pointer and not the thing itself.** The panel used to be where a recommendation was read —
 * it carried Clive's whole explanation while the log carried a one-line reference back to it,
 * which put the reasoning in the narrow column and split one argument across two panels. The log
 * holds the argument now, citations and all, and this is the index into it.
 *
 * A waiting card offers Execute and Reject (the Clive Insights hook and footer); a settled one
 * says its state and offers at most a Preview of what it produced. Both show the exposure and the values, and a card
 * closed unexecuted with a note shows it as the rejection reason. A waiting card shows why CMS held it, where CMS
 * said. A card with no log entry is not clickable.
 *
 * The authored three-word summary and the one-line description are not here either: nothing
 * produces them yet, and the alternative — the first fifty words of the explanation — is the
 * truncation those fields exist to avoid.
 */
const ActionCard: React.FC<ActionCardProps> = ({ row, onActionUpdate, onSelectLogEntry, isSelected, onDeselect }) => {
  const { key, action, state, logEventId } = row;
  const model = useActionCard(action, onActionUpdate);

  return (
    <ActivityCard
      kind="recommendation"
      appearance={ACTION_STATE_APPEARANCE[state]}
      title={actionTitle(action)}
      subtitle={model.relatedExposureLabel}
      status={<ActionStateChip state={state} action={action} />}
      confidence={<ActionConfidence action={action} />}
      createdAt={action.created_at}
      footer={
        // The history icon closes the row, beside the card's own "Show in the log".
        <div className="flex items-center gap-8">
          <div className="min-w-0 flex-1">
            {ACTION_NEEDS_ATTENTION[state] ? (
              <ActionGestures action={action} model={model} />
            ) : isPreviewableOnceSettled(action) ? (
              <SettledActionPreview action={action} subtitle={model.relatedExposureLabel} />
            ) : null}
          </div>
          {hasRevisions(action) ? (
            <ActionHistory action={action} state={state} exposureLabel={model.relatedExposureLabel} />
          ) : null}
        </div>
      }
      onSelect={() => onSelectLogEntry(row)}
      controls={logEventId ? logEntryElementId(logEventId) : undefined}
      elementId={activityCardElementId(key)}
      isSelected={isSelected}
      onDeselect={onDeselect}
    >
      <PayloadList action={action} state={state} visibleLines={ACTIVITY_VALUE_VISIBLE_LINES} />
      <RejectionReason reason={actionWaitingReason(state, action)?.text ?? null} label={TEXTS.WAITING_REASON} />
      <RejectionReason reason={actionRejectionReason(state, action)} label={actionReasonLabel(state)} />
    </ActivityCard>
  );
};

export default ActionCard;
